import { useLazyQuery, useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useContext, useState } from "react";
import { v4 } from "uuid";
import { SAVE_PROJECT } from "../../projects/graphql";
import GTMCostView from "../GTMCostView";
import { GTMProjectContext } from "../GTMProjectContext";
import { FETCH_GTM_SOURCE_PRODUCT_IMPORT_DATA } from "../graphql";
import StartUpProjectProductView from "./StartUpProjectProductView";

const COST_COMPANY = "AB";
const DEFAULT_CURRENCY = "CNY";
const CURRENCY_MAP = {
  RMB: "CNY",
};

function buildComponentPrices(activePrices = []) {
  const firstPriceCurrency =
    CURRENCY_MAP[activePrices[0]?.currency] ||
    activePrices[0]?.currency ||
    DEFAULT_CURRENCY;
  const companyPrices = activePrices.filter((p) => p.company === COST_COMPANY);
  const supplier = companyPrices.find((p) => p.supplier)?.supplier || null;
  const selectedSupplierPrices = supplier
    ? companyPrices.filter((p) => p.supplier?.id === supplier.id)
    : companyPrices;

  const prices = selectedSupplierPrices.map((p) => ({
    minQty: p.minQty,
    price: p.price,
  }));

  if (prices.length > 0) {
    return {
      prices,
      supplier,
      currency:
        CURRENCY_MAP[selectedSupplierPrices[0]?.currency] ||
        selectedSupplierPrices[0]?.currency ||
        firstPriceCurrency,
    };
  }

  return {
    prices: [{ minQty: 1, price: 0 }],
    supplier: null,
    currency: firstPriceCurrency,
  };
}

function buildImportedComponent({ name, qty, activePrices, sourceProductId }) {
  const { prices, supplier, currency } = buildComponentPrices(activePrices);
  const component = {
    id: v4(),
    name,
    qty,
    prices,
    type: "Product",
    currency,
    sourceProductId,
  };

  if (supplier) {
    component.supplierId = supplier.id;
    component.supplierName = supplier.name;
  }

  return component;
}

function buildImportedSize(sourceProduct) {
  return {
    shape: sourceProduct.shape || "cube",
    ballDiameter: sourceProduct.ballDiameter,
    cubeX: sourceProduct.cubeX,
    cubeY: sourceProduct.cubeY,
    cubeZ: sourceProduct.cubeZ,
    cylindricalDiameter: sourceProduct.cylindricalDiameter,
    cylindricalHeight: sourceProduct.cylindricalHeight,
    flatX: sourceProduct.flatX,
    flatY: sourceProduct.flatY,
    multiSizes: sourceProduct.multiSizes,
  };
}

function buildImportedStartUpData(sourceProduct, originalData) {
  const components = (sourceProduct.children || []).map((child) =>
    buildImportedComponent({
      name: child.product.name,
      qty: child.qty || 1,
      activePrices: child.product.activePrices || [],
      sourceProductId: child.product.id,
    }),
  );

  const orderQty = Number(sourceProduct.defaultQty) || 3000;
  const bomQty = Number(sourceProduct.itemsPerSet) || 1;
  const packagingComponents = components.map((component) => ({
    id: component.id,
    name: component.name,
    qty: component.qty,
    type: component.type || "Product",
    selected: true,
    sourceProductId: component.sourceProductId,
  }));

  console.log("packagingComponents", packagingComponents);

  const pantone = Array.isArray(sourceProduct.pantone)
    ? sourceProduct.pantone.filter(Boolean).join(", ")
    : sourceProduct.pantone || "";

  return {
    ...originalData,
    sourceProductId: sourceProduct.id,
    size: buildImportedSize(sourceProduct),
    weight: sourceProduct.itemWeight || 0,
    pantone,
    materials: (sourceProduct.productLine?.materials || [])
      .map((item) => item.material?.id)
      .filter(Boolean)
      .map((materialId) => ({
        materialId,
        weight: 0,
      })),
    remark: `Imported from source product [${sourceProduct.number}${sourceProduct.version ? ` | v${sourceProduct.version}` : ""}] ${sourceProduct.name}.`,
    components,
    orderQty,
    packagings:
      packagingComponents.length > 0
        ? [
            {
              id: v4(),
              packagingType: sourceProduct.packing || "Color Box",
              bomQty,
              orderQty,
              components: packagingComponents,
            },
          ]
        : [],
  };
}

const ProductView = () => {
  const {
    originalGTMProject,
    allMaterials,
    autoSave,
    saveProject,
    saveProjectRes,
    setAutoSaved,
    refetch,
  } = useContext(GTMProjectContext);
  const [fetchSourceProduct, fetchSourceProductRes] = useLazyQuery(
    FETCH_GTM_SOURCE_PRODUCT_IMPORT_DATA,
    {
      fetchPolicy: "network-only",
    },
  );
  const [saveStartUpProject, saveStartUpProjectRes] = useMutation(
    SAVE_PROJECT,
    {
      onCompleted() {
        setAutoSaved(true);
        Alert("success", "Source product info imported.");
      },
      onError(error) {
        Alert("error", parseError(error));
      },
    },
  );

  const [data, setData] = useState({
    productInitialSampleSent: originalGTMProject.productInitialSampleSent,
    productFinalSampleSent: originalGTMProject.productFinalSampleSent,
  });

  function onChange(key, value) {
    const newData = { ...data, [key]: value };
    setData(newData);
    autoSave({ id: originalGTMProject.id, input: newData });
  }

  function importSourceProductInfoHandler() {
    if (!originalGTMProject.sourceProduct?.id) {
      Alert("error", "Please choose a source product first.");
      return;
    }
    const sourceProductLabel = `[${originalGTMProject.sourceProduct.number}] ${originalGTMProject.sourceProduct.name}`;
    if (
      !window.confirm(
        `Are you sure you want to import source product info to this GTM project?\n\nSource Product: ${sourceProductLabel}`,
      )
    ) {
      return;
    }

    fetchSourceProduct({
      variables: { id: originalGTMProject.sourceProduct.id },
    }).then((res) => {
      const sourceProduct = res.data?.product;
      if (!sourceProduct) {
        Alert("error", "Source product not found.");
        return;
      }

      const originalData = JSON.parse(
        originalGTMProject.startUpProject.data || "{}",
      );
      const importedData = buildImportedStartUpData(
        sourceProduct,
        originalData,
      );

      saveStartUpProject({
        variables: {
          id: originalGTMProject.startUpProject.id,
          data: JSON.stringify(importedData, " ", 2),
        },
      }).then(() => {
        refetch();
      });
    });
  }

  function confirmHandler() {
    if (
      window.confirm(
        "Are you sure you want to confirm the Product Information for this project?",
      )
    ) {
      saveProject({
        variables: {
          id: originalGTMProject.id,
          productConfirmedAt: new Date().toISOString(),
        },
      });
    }
  }

  const disabled = originalGTMProject.productConfirmedAt;

  return (
    <div className="card relative">
      <div className="flex justify-between">
        <h5>
          Product (
          <b className="text-orange-500">
            {originalGTMProject.startUpProject.owner.firstName}
          </b>
          )
        </h5>

        <div>
          <Button
            onClick={importSourceProductInfoHandler}
            loading={
              fetchSourceProductRes.loading || saveStartUpProjectRes.loading
            }
            // disabled={
            //   !originalGTMProject.sourceProduct?.id ||
            //   disabled ||
            //   originalGTMProject.state !== "PACKAGING"
            // }
          >
            Import Source Product Info
          </Button>
        </div>
      </div>

      {originalGTMProject.productConfirmedAt && (
        <div className="text-green-600 font-bold mt-1">
          🟢 Confirmed at{" "}
          {new Date(originalGTMProject.productConfirmedAt).toLocaleString()}.
        </div>
      )}

      <div className="-mx-2 mt-4">
        <StartUpProjectProductView
          project={originalGTMProject.startUpProject}
          allMaterials={allMaterials}
        />
      </div>

      <div className="mt-6">
        <GTMCostView />
      </div>

      <div className="-mx-2 mt-6 whitespace-nowrap">
        <table>
          <thead>
            <tr>
              <th>Samples</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Initial Product Samples Sent:</td>
              <td className="w-full">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={data.productInitialSampleSent || false}
                    onChange={(e) =>
                      onChange("productInitialSampleSent", e.target.checked)
                    }
                    disabled={disabled}
                  />
                </div>
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>Final Product Samples Sent:</td>
              <td className="w-full">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={data.productFinalSampleSent || false}
                    onChange={(e) =>
                      onChange("productFinalSampleSent", e.target.checked)
                    }
                    disabled={disabled}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!originalGTMProject.productConfirmedAt && (
        <div className="mt-4 flex justify-end">
          <Button onClick={confirmHandler} loading={saveProjectRes.loading}>
            Confirm Product Information
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductView;
